import React, { useState } from 'react';
import Reveal from '../fx/Reveal';
import Icon from '../common/Icon';
import { CONTACT, LOCATION } from '../../data/siteContent';

const NEEDS = ['Website / Web App', 'Mobile App', 'SaaS / MVP', 'AI / Automation', 'Cloud / DevOps', 'Enterprise / ERP', 'Other'];

const API_URL = process.env.REACT_APP_API_URL || 'https://quantum-stack.com';

/**
 * LeadSection — conversion block shown above the footer on every public page.
 * Posts to the existing /api/contact endpoint (same payload as the Contact page).
 */
function LeadSection() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '', website: '' });
  const [needs, setNeeds] = useState([]);
  const [state, setState] = useState('idle'); // idle | sending | done | error
  const [error, setError] = useState('');

  const set = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const toggle = (n) => setNeeds((cur) => (cur.includes(n) ? cur.filter((x) => x !== n) : [...cur, n]));

  const submit = async (e) => {
    e.preventDefault();
    if (form.website) return; // honeypot
    if (!form.name.trim()) return setError('Please enter your name.');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return setError('Please enter a valid email address.');
    setError('');
    setState('sending');

    const prefix = needs.length ? `[Quick enquiry: ${needs.join(', ')}]` : '[Quick enquiry]';
    const payload = {
      name: form.name.trim(),
      email: form.email.trim(),
      company: null,
      phone: form.phone.trim() || null,
      message: `${prefix}\n\n${form.message.trim() || 'Please contact me about my project.'}\n\nSent from: ${window.location.pathname}`,
    };

    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setState('done');
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data?.message || 'Something went wrong. Please call or WhatsApp us instead.');
        setState('error');
      }
    } catch {
      setError('Unable to reach the server. Please call or WhatsApp us instead.');
      setState('error');
    }
  };

  return (
    <section className="qx-lead" id="get-quote" aria-labelledby="lead-heading">
      <div className="qx-lead__inner">
        <Reveal className="qx-lead__pitch">
          <span className="section-label">Free Consultation</span>
          <h2 className="qx-lead__title" id="lead-heading">Tell us what you want to build.</h2>
          <p className="qx-lead__sub">
            Startup idea, growing product or enterprise platform — share a few details and our team will
            come back with a clear plan, the right technology and a transparent estimate.
          </p>
          <ul className="qx-lead__points">
            <li>Free consultation &amp; no-obligation quote</li>
            <li>Fast response from a senior engineer, not a salesperson</li>
            <li>NDA on request · 100% IP ownership is yours</li>
            <li>Fixed-price, dedicated team or flexible hourly</li>
          </ul>
          <div className="qx-lead__channels">
            <a href={CONTACT.phoneHref} className="qx-chip"><Icon name="phone" size={16} /> {CONTACT.phone}</a>
            <a href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer" className="qx-chip"><Icon name="chat" size={16} /> WhatsApp</a>
            <a href={`mailto:${CONTACT.email}`} className="qx-chip"><Icon name="mail" size={16} /> {CONTACT.email}</a>
            <a href={LOCATION.mapsUrl} target="_blank" rel="noopener noreferrer" className="qx-chip"><Icon name="pin" size={16} /> {LOCATION.short}</a>
          </div>
        </Reveal>

        <Reveal delay={120} className="qx-lead__card">
          {state === 'done' ? (
            <div className="qx-lead__done" role="status">
              <span className="qx-lead__tick" aria-hidden="true">✓</span>
              <h3>Thank you — we&apos;ve got your enquiry.</h3>
              <p>Our team will review it and get back to you shortly. Need it sooner? Call or WhatsApp us on {CONTACT.phone}.</p>
            </div>
          ) : (
            <form onSubmit={submit} noValidate>
              <h3 className="qx-lead__formtitle">Get your free quote</h3>

              <fieldset className="qx-lead__needs">
                <legend>What do you need?</legend>
                <div>
                  {NEEDS.map((n) => (
                    <button
                      key={n}
                      type="button"
                      className={`qx-need${needs.includes(n) ? ' is-on' : ''}`}
                      aria-pressed={needs.includes(n)}
                      onClick={() => toggle(n)}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </fieldset>

              <div className="qx-lead__row">
                <label>
                  <span>Name *</span>
                  <input name="name" value={form.name} onChange={set} autoComplete="name" placeholder="Your name" />
                </label>
                <label>
                  <span>Email *</span>
                  <input name="email" type="email" value={form.email} onChange={set} autoComplete="email" placeholder="you@company.com" />
                </label>
              </div>
              <label>
                <span>Phone / WhatsApp</span>
                <input name="phone" type="tel" value={form.phone} onChange={set} autoComplete="tel" placeholder="+91 …" />
              </label>
              <label>
                <span>About your project</span>
                <textarea name="message" rows="3" value={form.message} onChange={set} placeholder="A sentence or two is enough — goals, timeline, budget range…" />
              </label>
              {/* honeypot */}
              <input name="website" value={form.website} onChange={set} tabIndex="-1" autoComplete="off" aria-hidden="true" className="qx-hp" />

              {error && <p className="qx-lead__error" role="alert">{error}</p>}

              <button type="submit" className="btn-primary-brand qx-lead__submit" disabled={state === 'sending'}>
                {state === 'sending' ? 'Sending…' : 'Get my free consultation →'}
              </button>
              <p className="qx-lead__fine">No spam. We only use your details to reply to this enquiry.</p>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}

export default LeadSection;
